import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../components/site/Page";

const title = "Junta de Andalucía och utbildning | SIS Estepona";
const description =
  "Junta de Andalucía är utbildningsmyndigheten i Andalusien. Så fungerar tillstånd, minimikrav på lokaler och säkerhet samt den löpande tillsynen av skolor.";

export const Route = createFileRoute("/junta-de-andalucia-och-utbildning")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: absoluteUrl("/junta-de-andalucia-och-utbildning") },
      { property: "og:locale", content: "sv_SE" },
      ...ogImage(),
    ],
    links: seoLinks("/junta-de-andalucia-och-utbildning"),
  }),
  component: Junta,
});

function Junta() {
  return (
    <>
      <PageHero
        eyebrow="Tillsynsmyndighet"
        title="Junta de Andalucía – den spanska utbildningsmyndigheten"
        intro="Junta de Andalucía är den regionala regeringen i Andalusien och dess utbildningsdepartement är den myndighet som ger tillstånd till, och utövar tillsyn över, alla skolor som drivs i regionen – ungefär den roll Skolverket och Skolinspektionen har i Sverige."
      />

      <Prose>
        <p>
          Genom sitt utbildningsdepartement (Consejería de Desarrollo Educativo y Formación
          Profesional) arbetar Junta de Andalucía för att säkerställa att alla barn och unga i
          Andalusien får tillgång till en kvalitativ och inkluderande utbildning – och för att varje
          skola i regionen uppfyller de krav som lagen ställer.
        </p>

        <h2>Gäller det här alla skolor i Spanien?</h2>
        <p>
          Ja. Utbildning regleras nationellt men förvaltas av Spaniens 17 autonoma regioner. Alla
          skolor – offentliga, halvoffentliga (concertados) och helt privata – måste ha
          administrativt tillstånd från sin regions utbildningsmyndighet för att få öppna och
          bedriva undervisning, och alla står under den spanska utbildningsinspektionen, Inspección
          Educativa. För oss i Andalusien är den myndigheten Junta de Andalucía. Skolor som
          undervisar enligt ett annat lands system, som vi, omfattas dessutom av den särskilda
          regleringen för utländska skolor i Spanien (Real Decreto 806/1993).
        </p>

        <h2>Vad tillsynen innebär i praktiken</h2>
        <p>
          <strong>Innan skolan får öppna.</strong> Tillstånd ges först när skolan uppfyller de
          nationella minimikraven i Real Decreto 132/2010. De reglerar bland annat lokalernas
          utformning och yta per elev, klassrummens och skolgårdens storlek, hygien- och
          säkerhetskrav, tillgänglighet, maximalt antal elever per grupp samt personalens
          behörighet. Varje förändring av lokaler, årskurser eller antal platser kräver ett nytt
          beslut från myndigheten.
        </p>
        <p>
          <strong>Löpande tillsyn.</strong> Efter tillståndet står skolan under Inspección
          Educativa. Uppdraget står i den spanska skollagen (LOE, artikel 151): att övervaka och
          kontrollera skolans pedagogiska och organisatoriska verksamhet, följa upp lärarnas och
          skolledningens arbete, kontrollera att lagar och regler följs och delta i utvärderingen av
          skolsystemet. Inspektörerna har rätt att komma till skolan, ta del av dokumentation och
          samtala med personal.
        </p>
        <p>
          <strong>Så planeras arbetet.</strong> I Andalusien arbetar inspektionen efter en flerårig
          plan – Plan General de Actuación 2023–2027 – som bryts ned i årliga planer per provins.
          Varje skola har en tilldelad inspektör som är skolans fasta kontakt hos myndigheten.
        </p>
        <p>
          <strong>Krav som gäller oss.</strong> Utöver lokaler och säkerhet ställer myndigheten krav
          på spanska språket och spansk kultur i undervisningen. Därför är spanskan en självklar del av varje dag, från förskolan och uppåt. Vår målsättning är att all
          svensk undervisning ges av lärare med svensk lärarlegitimation.
        </p>
        <p>
          <strong>Vad tillsynen inte omfattar.</strong> Junta de Andalucía granskar inte den svenska
          läroplanens innehåll. Den svenska delen vilar på svenska styrdokument och på vårt
          godkännande från Skolverket för kompletterande svenska – vi är inte en
          statsbidragsberättigad svensk utlandsskola.
        </p>
        <p>
          Källor:{" "}
          <a
            className="text-primary link-quiet"
            href="https://www.boe.es/buscar/act.php?id=BOE-A-2010-4132"
            target="_blank"
            rel="noopener noreferrer"
          >
            Real Decreto 132/2010 (BOE)
          </a>
          ,{" "}
          <a
            className="text-primary link-quiet"
            href="https://www.juntadeandalucia.es/boja/2023/146/2"
            target="_blank"
            rel="noopener noreferrer"
          >
            Plan General de Actuación 2023–2027 (BOJA)
          </a>{" "}
          och{" "}
          <a
            className="text-primary link-quiet"
            href="https://www.juntadeandalucia.es/educacion/portales/web/inspeccion-educativa/w/funciones-y-atribuciones"
            target="_blank"
            rel="noopener noreferrer"
          >
            Inspección Educativas uppdrag
          </a>
          .
        </p>


        <h2>Utbildningsinsatser och program</h2>
        <ul>
          <li>
            <strong>Grund- och gymnasieutbildning:</strong> Junta de Andalucía övervakar och
            administrerar grund- och gymnasieskolor i regionen och säkerställer att skolorna följer
            nationella och regionala läroplaner.
          </li>
          <li>
            <strong>Förskoleutbildning:</strong> Regionen erbjuder omfattande förskoleprogram för barn
            i åldrarna 0–6 år som främjar tidig barndomsutveckling.
          </li>
          <li>
            <strong>Specialpedagogik:</strong> Stöd och resurser för elever med särskilda
            utbildningsbehov, inklusive anpassade undervisningsmetoder.
          </li>
          <li>
            <strong>Digitalisering och innovation:</strong> Digitala verktyg och resurser till skolor,
            interaktiva klassrum, lärplattformar och utbildning för lärare i digital pedagogik.
          </li>
          <li>
            <strong>Lärarfortbildning:</strong> Kontinuerlig fortbildning och professionell utveckling
            för lärare och skolledare.
          </li>
          <li>
            <strong>Kulturell och språklig mångfald:</strong> Program som stödjer elever från olika
            bakgrunder och undervisning i flera språk.
          </li>
          <li>
            <strong>Hälsa och välmående:</strong> Hälsoundervisning, fysiska aktivitetsprogram och
            initiativ för en säker och stödjande skolmiljö.
          </li>
        </ul>

        <h2>Samarbete och partnerskap</h2>
        <p>
          Junta de Andalucía arbetar nära med skolor, kommuner och andra utbildningsinstitutioner för
          att skapa en sammanhållen och effektiv utbildningsstruktur. Swedish International School har
          tillstånd och står under tillsyn av Junta de Andalucía.
        </p>
        <p>
          <a href="https://www.juntadeandalucia.es/" target="_blank" rel="noopener noreferrer">
            Läs mer om Junta de Andalucía här
          </a>
        </p>

        <h2>Vad detta betyder för svenska familjer</h2>
        <p>
          För familjer som flyttar hit är det bra att känna till att utbildningsfrågor i Spanien till
          stor del hanteras regionalt. Skolplikt, läsårets upplägg och lokala helgdagar följer
          Andalusiens regler, vilket bland annat påverkar när skolor har lov och hur kontakten med
          kommunen ser ut.
        </p>
        <p>
          Vår undervisning följer den svenska läroplanen, men vi lever i den lokala kontexten: spanska
          finns med på schemat från förskolan, och skolans kalender tar hänsyn till både svenska och
          andalusiska helgdagar. Det gör övergången enklare oavsett om familjen stannar länge eller
          bara en period.
        </p>

        <h2>Praktiskt när ni flyttar hit</h2>
        <p>
          Flytten till Spanien innehåller flera myndighetssteg som NIE-nummer, empadronamiento och i
          vissa fall sjukvårdsregistrering. Vi går igenom det vi kan i vår guide om{" "}
          <a className="text-primary link-quiet" href="/nie-och-folkbokforing-i-spanien">NIE och folkbokföring</a>{" "}
          och i guiden om att{" "}
          <a className="text-primary link-quiet" href="/flytta-till-spanien-med-barn">flytta till Spanien med barn</a>.
          För myndighetsbeslut hänvisar vi alltid till Junta de Andalucía och er kommun.
        </p>


      </Prose>

      <CtaBand />
      <div className="h-20" />
    </>
  );
}
